<script setup>
import { groupBy } from '@/utils/array'
import { asyncComputed } from '@/utils/asyncComputed'
import { jwt } from '@/utils/jwt'
import { request } from '@/utils/request'
import { createTimer } from '@/utils/timer'
import { computed, onUnmounted, watch } from 'vue'

const props = defineProps({
    taskId: {
        type: [Number, String],
        required: true,
    },
    seconds: {
        type: Number,
        default: 300,
    },
})

const emit = defineEmits(['update:status'])

const task = asyncComputed(async () => {
    const { ok, content } = await request.post(`/api/manage/tasks/claims/${props.taskId}`, null, null, {
        seconds: props.seconds,
    })

    if (ok && content.task) {
        content.task.claimUser = content.user
        return content.task
    }

    return null
}, [])

let timer = createTimer(task.update, props.seconds * 500)

watch(
    () => props.seconds,
    () => {
        timer?.destroy()
        timer = createTimer(task.update, props.seconds * 500)
    }
)

async function releaseClaims() {
    if (owned.value) {
        await request.delete('/api/manage/tasks/claims', task.value.id)
    }
}

onUnmounted(releaseClaims)

const owned = computed(() => task.value && task.value.claimUserId === jwt.id)

const message = computed(() => {
    if (task.updating) {
        return 'Updating...'
    }

    if (!task.value) {
        return 'Task not found'
    }

    if (owned.value) {
        return 'You have this task claimed'
    }

    if (task.value.completed) {
        return 'Task completed'
    }

    return `${task.value.claimUser.firstName} ${task.value.claimUser.lastName} has this task claimed`
})

const status = computed(() => {
    if (task.updating) {
        return 'none'
    }

    if (!task.value || task.value.completed) {
        return 'warning'
    }

    if (!owned.value) {
        return 'conflict'
    }

    return 'owned'
})

watch(status, () => emit('update:status', status.value))

function refresh() {
    task.update()
}

defineExpose({
    status,
    refresh,
})
</script>
<template>
    <span :data-status="status">
        <i>●</i>
        <div class="details-container">
            <ul class="details">
                <li>{{ message }}</li>
            </ul>
        </div>
    </span>
</template>
<style scoped>
[data-status='none'] i {
    color: gold;
}

[data-status='owned'] i {
    color: darkgreen;
}

[data-status='warning'] i {
    color: grey;
}

[data-status='conflict'] i {
    color: red;
}

.details-container {
    position: relative;
}

.details {
    background-color: white;
    box-shadow: var(--boxshadow-default);
    display: none;
    flex-direction: column;
    font-size: 12px;
    gap: 8px;
    left: 0;
    padding: 8px 16px;
    position: absolute;
    top: 0;
    white-space: nowrap;
}

.left .details {
    left: initial;
    right: 0;
}

[data-status]:hover .details {
    display: flex;
}
</style>
